import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchDivisionClimbers, ClimbersResponse } from '../services/LeagueStatsService';
import { getLatestSeason } from '../services/MatchService';
import Loading from '../components/Loading';
import PlayerImage from '../components/PlayerImage';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import styles from '../styles/StatsView.module.css';

const LeagueStatsClimbersPage = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ClimbersResponse | null>(null);
    const [fromSeason, setFromSeason] = useState(0);
    const [toSeason, setToSeason] = useState(0);
    const [displayCount, setDisplayCount] = useState<number>(10);
    const [error, setError] = useState<string | null>(null);
    const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);

    useEffect(() => {
        const init = async () => {
            try {
                const latest = await getLatestSeason();
                setFromSeason(latest - 1);
                setToSeason(latest);
                const seasons = [];
                for (let s = 18; s <= latest; s++) {
                    seasons.push(s);
                }
                setAvailableSeasons(seasons.reverse());
            } catch (err) {
                console.error('Error fetching latest season:', err);
                setFromSeason(30);
                setToSeason(31);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (fromSeason > 0 && toSeason > 0) loadData();
    }, [fromSeason, toSeason]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchDivisionClimbers(fromSeason, toSeason);
            setData(response);
        } catch (err) {
            console.error('Error loading climbers data:', err);
            setError('Kunne ikke hente data');
        } finally {
            setLoading(false);
        }
    };

    const getDivisionColor = (divisionName: string) => {
        if (divisionName.includes('Ligaen')) return '#ffd700';
        if (divisionName.includes('1.')) return '#c0c0c0';
        if (divisionName.includes('2.')) return '#cd7f32';
        if (divisionName.includes('3.')) return '#6c757d';
        if (divisionName.includes('4.')) return '#495057';
        if (divisionName.includes('5.')) return '#343a40';
        return '#6c757d';
    };

    const formatDivision = (div: string) => {
        // Remove any 'Season X - ' prefix
        const cleanDiv = div.replace(/Season \d+ - /, '');
        
        const divMap: { [key: string]: string } = {
            'Ligaen': 'Ligaen',
            '1. Division': '1. Div',
            '2. Division': '2. Div',
            '3. Division': '3. Div',
            '4. Division': '4. Div',
            '5. Division': '5. Div',
        };
        
        for (const [key, value] of Object.entries(divMap)) {
            if (cleanDiv.includes(key)) {
                const suffix = cleanDiv.replace(key, '').trim();
                return suffix ? `${value} ${suffix}` : value;
            }
        }
        return cleanDiv;
    };

    const getRankClass = (index: number) => {
        switch(index) {
            case 0: return styles.rank1;
            case 1: return styles.rank2;
            case 2: return styles.rank3;
            default: return styles.rankOther;
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="text-center mt-5 text-white">No data available</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.twitterCard}>
                {/* Compact Header */}
                <div className={styles.compactHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.titleSection}>
                            <h1 className={styles.mainTitle}>Division Klatrere & Faldere</h1>
                            <div className={styles.subtitle}>De største spring mellem sæsoner</div>
                        </div>
                        <div className={styles.seasonBadge}>S{fromSeason} → S{toSeason}</div>
                    </div>
                </div>

                {/* Split Layout */}
                <div className={styles.splitContainer}>
                    {/* Climbers Column */}
                    <div className={styles.column}>
                        <div className={styles.columnHeader}>
                            <div className={`${styles.columnIcon} ${styles.gainIcon}`}>
                                <TrendingUp style={{ color: '#28a745' }} />
                            </div>
                            <div className={styles.columnTitle}>
                                <h3>Top Klatrere</h3>
                                <div className={styles.columnSubtitle}>Største spring opad</div>
                            </div>
                        </div>
                        <div className={styles.playerList}>
                            {data.climbers.slice(0, displayCount).map((climber, index) => (
                                <div key={climber.steamId} className={`${styles.playerCard} ${styles.gainCard}`}>
                                    <div className={`${styles.rankBadge} ${getRankClass(index)}`}>
                                        {index + 1}
                                    </div>
                                    <div className={styles.playerImage}>
                                        <PlayerImage 
                                            id={climber.steamId} 
                                            width={50}
                                        />
                                    </div>
                                    <div className={styles.playerInfo}>
                                        <Link to={`/player/${climber.steamId}`} className={styles.playerName}>
                                            {climber.playerName}
                                        </Link>
                                        <div className={styles.teamInfo}>
                                            <span>{climber.currentTeam || 'Ingen hold'}</span>
                                        </div>
                                        <div style={{ marginTop: '4px', fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.4)' }}>
                                            <span style={{
                                                backgroundColor: getDivisionColor(climber.fromDivisionName) + '33',
                                                color: getDivisionColor(climber.fromDivisionName),
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                marginRight: '4px'
                                            }}>
                                                {formatDivision(climber.fromDivisionName)}
                                            </span>
                                            →
                                            <span style={{
                                                backgroundColor: getDivisionColor(climber.toDivisionName) + '33',
                                                color: getDivisionColor(climber.toDivisionName),
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                marginLeft: '4px'
                                            }}>
                                                {formatDivision(climber.toDivisionName)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.ratingStats}>
                                        <div className={`${styles.changeValue} ${styles.gainValue}`}>
                                            +{climber.divisionsJumped}
                                        </div>
                                        <div className={styles.ratingValues}>
                                            {climber.jumpDescription}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Fallers Column */}
                    <div className={styles.column}>
                        <div className={styles.columnHeader}>
                            <div className={`${styles.columnIcon} ${styles.lossIcon}`}>
                                <TrendingDown style={{ color: '#dc3545' }} />
                            </div>
                            <div className={styles.columnTitle}>
                                <h3>Top Faldere</h3>
                                <div className={styles.columnSubtitle}>Største fald nedad</div>
                            </div>
                        </div>
                        <div className={styles.playerList}>
                            {data.fallers.slice(0, displayCount).map((faller, index) => (
                                <div key={faller.steamId} className={`${styles.playerCard} ${styles.lossCard}`}>
                                    <div className={`${styles.rankBadge} ${getRankClass(index)}`}>
                                        {index + 1}
                                    </div>
                                    <div className={styles.playerImage}>
                                        <PlayerImage 
                                            id={faller.steamId} 
                                            width={50}
                                        />
                                    </div>
                                    <div className={styles.playerInfo}>
                                        <Link to={`/player/${faller.steamId}`} className={styles.playerName}>
                                            {faller.playerName}
                                        </Link>
                                        <div className={styles.teamInfo}>
                                            <span>{faller.currentTeam || 'Ingen hold'}</span>
                                        </div>
                                        <div style={{ marginTop: '4px', fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.4)' }}>
                                            <span style={{
                                                backgroundColor: getDivisionColor(faller.fromDivisionName) + '33',
                                                color: getDivisionColor(faller.fromDivisionName),
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                marginRight: '4px'
                                            }}>
                                                {formatDivision(faller.fromDivisionName)}
                                            </span>
                                            →
                                            <span style={{
                                                backgroundColor: getDivisionColor(faller.toDivisionName) + '33',
                                                color: getDivisionColor(faller.toDivisionName),
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                marginLeft: '4px'
                                            }}>
                                                {formatDivision(faller.toDivisionName)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.ratingStats}>
                                        <div className={`${styles.changeValue} ${styles.lossValue}`}>
                                            -{faller.divisionsJumped}
                                        </div>
                                        <div className={styles.ratingValues}>
                                            {faller.fallDescription}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Info Box */}
                <div className={styles.infoBox}>
                    <strong>Division Spring:</strong> Viser spillere med de største divisionsændringer mellem sæson {fromSeason} og {toSeason}. 
                    Positive tal indikerer oprykning, negative tal indikerer nedrykning.
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <div className={styles.footerText}>
                        <span className={styles.highlight}>PowerStats.dk</span> • Division Progression Analyse • S{fromSeason} → S{toSeason}
                    </div>
                </div>

                {/* Filter Controls - Hidden at bottom */}
                <div className={styles.filterControls}>
                    <div className={styles.filterBar}>
                        <select 
                            className={styles.filterSelect}
                            value={fromSeason}
                            onChange={(e) => setFromSeason(Number(e.target.value))}
                        >
                            {availableSeasons.map(s => (
                                <option key={s} value={s}>Fra Sæson {s}</option>
                            ))}
                        </select>
                        <select 
                            className={styles.filterSelect}
                            value={toSeason}
                            onChange={(e) => setToSeason(Number(e.target.value))}
                        >
                            {availableSeasons.map(s => (
                                <option key={s} value={s}>Til Sæson {s}</option>
                            ))}
                        </select>
                        <select 
                            className={styles.filterSelect}
                            value={displayCount}
                            onChange={(e) => setDisplayCount(Number(e.target.value))}
                        >
                            <option value={5}>Vis Top 5</option>
                            <option value={10}>Vis Top 10</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeagueStatsClimbersPage;